import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ShoppingCart, Trash2, Clock, Sparkles, Loader2, AlertCircle, Package, Store, Users, Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
// iter238 (Variante B §6) · Kauf-Consent-Checkbox für SOCIAL
import PurchaseConsentCheckbox from "@/components/paid-media/PurchaseConsentCheckbox";

function formatCountdown(expiresAt, expiredLabel) {
  if (!expiresAt) return null;
  const remain = new Date(expiresAt).getTime() - Date.now();
  if (remain <= 0) return expiredLabel;
  const m = Math.floor(remain / 60000);
  const s = Math.floor((remain % 60000) / 1000);
  return `${m}:${String(s).padStart(2, "0")}`;
}

// iter131b: Cart wird nach Bereich gruppiert — 1 Bereich = 1 Checkout-Bündel.
const AREA_META = {
  MARKETPLACE: {
    label: "Marketplace",
    icon: Store,
    testId: "cart-section-marketplace",
    checkoutTarget: "/cart/checkout?area=MARKETPLACE",
  },
  STILLTOUCHEDX: {
    label: "stilltouchedX",
    icon: Package,
    testId: "cart-section-stx",
    checkoutTarget: "/cart/checkout?area=STILLTOUCHEDX",
  },
  SOCIAL: {
    label: "Social · Digital",
    icon: Sparkles,
    testId: "cart-section-social",
    checkoutTarget: null,  // Social nutzt direkten POST /cart/social/checkout
  },
};

function itemArea(item) {
  if (item.source_type === "PAID_MEDIA_ASSET") return "SOCIAL";
  return item.area_scope || "MARKETPLACE";
}

export default function CartPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({ items: [], total_amount: 0, count: 0 });
  const [removing, setRemoving] = useState({});
  const [checkoutBusy, setCheckoutBusy] = useState(null);
  const [tick, setTick] = useState(0);
  // iter139: Scarcity-Signale je Listing-ID
  const [scarcity, setScarcity] = useState({}); // { [listing_id]: {in_other_carts, locked_by_other, ...} }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cart");
      setCart(data);
      window.dispatchEvent(new Event("cart-refresh"));
      // iter139: Scarcity-Signale für alle LISTING-Items laden
      const listingIds = (data.items || [])
        .filter((it) => it.source_type === "LISTING" && it.listing_id)
        .map((it) => it.listing_id);
      if (listingIds.length > 0) {
        try {
          const { data: sc } = await api.post("/cart/scarcity", { listing_ids: listingIds });
          const map = {};
          for (const sig of (sc.signals || [])) map[sig.listing_id] = sig;
          setScarcity(map);
        } catch (e) { /* ignore scarcity errors */ }
      } else {
        setScarcity({});
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate("/login?next=/cart");
      return;
    }
    load();
    const iv = setInterval(() => setTick((s) => s + 1), 1000);
    // iter139: alle 30s Scarcity-Signale refreshen
    const scIv = setInterval(async () => {
      const listingIds = (cart.items || [])
        .filter((it) => it.source_type === "LISTING" && it.listing_id)
        .map((it) => it.listing_id);
      if (listingIds.length === 0) return;
      try {
        const { data: sc } = await api.post("/cart/scarcity", { listing_ids: listingIds });
        const map = {};
        for (const sig of (sc.signals || [])) map[sig.listing_id] = sig;
        setScarcity(map);
      } catch (e) { /* ignore */ }
    }, 30000);
    return () => { clearInterval(iv); clearInterval(scIv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const grouped = useMemo(() => {
    const g = { MARKETPLACE: [], STILLTOUCHEDX: [], SOCIAL: [] };
    for (const it of (cart.items || [])) g[itemArea(it)].push(it);
    return g;
  }, [cart.items]);

  const handleRemove = async (cartItemId) => {
    if (!window.confirm(t("cart.confirm_remove"))) return;
    setRemoving((s) => ({ ...s, [cartItemId]: true }));
    try {
      await api.delete(`/cart/items/${cartItemId}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || t("cart.remove_failed"));
    } finally {
      setRemoving((s) => ({ ...s, [cartItemId]: false }));
    }
  };

  const [socialConsent, setSocialConsent] = useState(false);
  // iter260 · Zahlungsart-Auswahl-Dialog für Social/Digital.
  const [methodModal, setMethodModal] = useState({ open: false, methods: [], busy: false });

  const METHOD_LABELS = {
    STRIPE: { label: t("cart.pay_stripe", { defaultValue: "Kredit-/Debitkarte (Stripe)" }), sub: t("cart.pay_instant", { defaultValue: "Sofort freigeschaltet" }) },
    PAYPAL: { label: t("cart.pay_paypal", { defaultValue: "PayPal" }), sub: t("cart.pay_instant", { defaultValue: "Sofort freigeschaltet" }) },
    BANK_TRANSFER: { label: t("cart.pay_bank", { defaultValue: "Überweisung / Vorkasse" }), sub: t("cart.pay_bank_sub", { defaultValue: "Freischaltung nach Zahlungseingang" }) },
  };

  const checkoutArea = async (area) => {
    if (area !== "SOCIAL") { navigate(`/cart/checkout?area=${area}`); return; }
    // iter238 · Purchase-Consent Pflicht (Blueprint §6).
    if (!socialConsent) {
      alert(t("cart.consent_required", { defaultValue: "Bitte bestätige die Nutzungsrechte, um Paid-Media zu kaufen." }));
      return;
    }
    setCheckoutBusy("SOCIAL");
    try {
      const { data: pm } = await api.get("/payments/methods");
      const active = (pm.methods || pm.active_methods || []).filter((m) => m.active !== false);
      const codes = active.map((m) => m.code).filter((c) => ["STRIPE", "PAYPAL", "BANK_TRANSFER"].includes(c));
      if (codes.length === 0) {
        alert(t("cart.no_payment_method", { defaultValue: "Keine Zahlungsart aktuell verfügbar. Bitte später erneut versuchen." }));
        return;
      }
      // iter260 · Immer den Auswahl-Dialog zeigen (kein Auto-Redirect mehr).
      setMethodModal({ open: true, methods: codes, busy: false });
    } catch (_e) {
      alert(t("cart.no_payment_method", { defaultValue: "Keine Zahlungsart aktuell verfügbar. Bitte später erneut versuchen." }));
    } finally {
      setCheckoutBusy(null);
    }
  };

  const doSocialCheckout = async (method) => {
    setMethodModal((s) => ({ ...s, busy: true }));
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/cart/social/checkout", {
        origin_url: origin,
        payment_method: method,
        buyer_email: user?.email,
        purchase_consent: true,
      });
      // Echte externe Stripe-Session (nur wenn nicht mock) → externer Redirect.
      if (data?.url && data?.mock !== true && method === "STRIPE") {
        window.location.href = data.url;
        return;
      }
      // MOCK (Stripe/PayPal) + Überweisung → interne Pay-Seite.
      try { sessionStorage.setItem(`pay_${data.session_id}`, JSON.stringify(data)); } catch (_e) { /* noop */ }
      setMethodModal({ open: false, methods: [], busy: false });
      navigate(`/checkout/pay?session_id=${data.session_id}&provider=${data.provider}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || t("cart.checkout_failed", { defaultValue: "Checkout fehlgeschlagen" }));
      alert(msg);
      setMethodModal((s) => ({ ...s, busy: false }));
    }
  };

  if (loading) {
    return (
      <div data-testid="cart-page-loading" className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 size={32} className="text-[#666] animate-spin" />
      </div>
    );
  }

  const areasWithItems = ["STILLTOUCHEDX", "MARKETPLACE", "SOCIAL"].filter((a) => grouped[a].length > 0);

  return (
    <div data-testid="cart-page" className="min-h-screen bg-white text-[#0A0A0A]">
      <div className="sticky top-0 z-40 flex items-center gap-3 px-4 h-14 bg-white border-b border-[#E5E5E5]">
        <button onClick={() => navigate(-1)} data-testid="cart-back-btn" className="text-[#666]">
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-2">
          <ShoppingCart size={20} />
          <h1 className="text-base font-bold">{t("cart.title")}</h1>
          {cart.count > 0 && (
            <span data-testid="cart-count-label" className="text-xs text-[#999]">
              · {cart.count} {t("cart.items_label")}
            </span>
          )}
        </div>
      </div>

      {cart.items.length === 0 ? (
        <div data-testid="cart-empty" className="flex flex-col items-center justify-center px-8 py-24 text-center">
          <ShoppingCart size={48} className="text-[#CCC] mb-4" />
          <p className="text-lg font-medium mb-1">{t("cart.empty_title")}</p>
          <p className="text-sm text-[#666] mb-6">{t("cart.empty_body")}</p>
          <div className="flex gap-2">
            <button
              data-testid="cart-goto-marketplace"
              onClick={() => navigate("/marketplace")}
              className="px-4 py-2 rounded-full bg-[#0A0A0A] text-white text-sm font-medium">
              {t("cart.goto_mp")}
            </button>
            <button
              data-testid="cart-goto-stx"
              onClick={() => navigate("/stilltouchedx")}
              className="px-4 py-2 rounded-full border border-[#E5E5E5] text-[#0A0A0A] text-sm font-medium">
              {t("cart.goto_stx")}
            </button>
          </div>
        </div>
      ) : (
        <div className="mx-auto max-w-2xl lg:max-w-3xl px-0 lg:px-6 py-4 space-y-6">
          {areasWithItems.map((area) => {
            const meta = AREA_META[area];
            const Icon = meta.icon;
            const items = grouped[area];
            const sectionTotal = items.reduce((sum, it) => sum + Number(it.price_total || 0), 0);
            const hasUnavailable = items.some((it) => it._unavailable);
            // iter139: Sperre den Bereichs-Checkout, wenn irgendein Unikat aktuell von einem anderen gehalten wird.
            const hasLockedByOther = items.some(
              (it) => it.source_type === "LISTING" && scarcity[it.listing_id]?.locked_by_other,
            );

            return (
              <section key={area} data-testid={meta.testId} className="rounded-2xl border border-[#E5E5E5] overflow-hidden bg-white">
                {/* Bereichs-Header */}
                <header className="flex items-center gap-2 px-4 py-3 bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <Icon size={14} className="text-[#0A0A0A]" />
                  <h2 className="text-[11px] font-bold uppercase tracking-wider text-[#0A0A0A]">{meta.label}</h2>
                  <span className="text-[11px] text-[#999]">· {items.length} {items.length === 1 ? "Artikel" : "Artikel"}</span>
                </header>

                {/* Items dieser Sektion */}
                <div className="divide-y divide-[#E5E5E5]">
                  {items.map((it) => {
                    const isMystery = it.source_type === "MYSTERY_RESERVATION";
                    const countdown = isMystery ? formatCountdown(it.expires_at, t("cart.expired")) : null;
                    const expired = countdown === t("cart.expired");
                    // eslint-disable-next-line no-unused-expressions
                    tick;
                    return (
                      <div
                        key={it.cart_item_id}
                        data-testid={`cart-item-${it.cart_item_id}`}
                        className="px-4 py-4 flex gap-3">
                        <div className="w-20 h-20 shrink-0 rounded-xl overflow-hidden bg-[#F5F5F5] flex items-center justify-center relative">
                          {it.thumb_url ? (
                            <>
                              <img src={it.thumb_url} alt="" className="w-full h-full object-cover" />
                              {it.locked_preview && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                  <Lock size={18} className="text-white drop-shadow" />
                                </div>
                              )}
                            </>
                          ) : it.locked_preview ? (
                            <Lock size={24} className="text-[#999]" />
                          ) : isMystery ? (
                            <Sparkles size={28} className="text-purple-500" />
                          ) : (
                            <ShoppingCart size={24} className="text-[#BBB]" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              {isMystery && (
                                <span data-testid={`cart-item-mystery-badge-${it.cart_item_id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-purple-600 mb-1">
                                  <Sparkles size={10} /> {t("cart.mystery_badge")}
                                </span>
                              )}
                              <p data-testid={`cart-item-title-${it.cart_item_id}`} className="text-sm font-medium truncate">
                                {it.title}
                              </p>
                              {it.seller_display_name && (
                                <p className="text-[11px] text-[#666] mt-0.5">{it.seller_display_name}</p>
                              )}
                              {(Array.isArray(it.selected_addons) && it.selected_addons.length > 0) && (
                                <ul data-testid={`cart-item-addons-${it.cart_item_id}`} className="mt-1 space-y-0.5">
                                  {it.selected_addons.map((a) => (
                                    <li key={a.code} className="text-[10px] text-[#666] flex justify-between gap-3">
                                      <span>+ {a.code.replace(/^OPT_/, "").replaceAll("_", " ").toLowerCase()}</span>
                                      <span className="font-mono">+{Number(a.price).toFixed(2)}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {it.selected_wear_days && (
                                <p data-testid={`cart-item-weardays-${it.cart_item_id}`} className="mt-1 text-[10px] text-[#666] flex justify-between gap-3">
                                  <span>+ {it.selected_wear_days.days} Tag{it.selected_wear_days.days === 1 ? "" : "e"} extra tragen</span>
                                  <span className="font-mono">+{Number(it.selected_wear_days.price).toFixed(2)}</span>
                                </p>
                              )}
                              {isMystery && countdown && (
                                <p
                                  data-testid={`cart-item-countdown-${it.cart_item_id}`}
                                  className={`text-[11px] mt-1 flex items-center gap-1 font-mono ${expired ? "text-red-600" : "text-amber-600"}`}>
                                  <Clock size={11} />
                                  {expired ? t("cart.reservation_expired") : t("cart.reserved_countdown", { time: countdown })}
                                </p>
                              )}
                              {it.discount_pct > 0 && (
                                <div data-testid={`cart-item-discount-${it.cart_item_id}`} className="mt-1 flex items-center gap-2 text-[10px]">
                                  <span className="line-through text-[#999]">{Number(it.price_original || 0).toFixed(2)} €</span>
                                  <span className="rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 font-bold">
                                    −{it.discount_pct} % {it.discount_reason ? `(${it.discount_reason})` : ""}
                                  </span>
                                </div>
                              )}
                              {/* iter139: Scarcity-Hint (Phase 1 = No-Lock, aber Warnung wenn viele/gesperrt) */}
                              {it.source_type === "LISTING" && scarcity[it.listing_id] && (
                                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                  {scarcity[it.listing_id].locked_by_other && (
                                    <span data-testid={`cart-item-locked-${it.cart_item_id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                                      <Lock size={10} />
                                      {t("cart.locked_by_other", { defaultValue: "Aktuell reserviert" })}
                                    </span>
                                  )}
                                  {!scarcity[it.listing_id].locked_by_other && scarcity[it.listing_id].in_other_carts > 0 && (
                                    <span data-testid={`cart-item-scarcity-${it.cart_item_id}`} className="inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                                      <Users size={10} />
                                      {t("cart.in_other_carts", {
                                        count: scarcity[it.listing_id].in_other_carts,
                                        defaultValue: `In {{count}} weiteren Warenkörben`,
                                      })}
                                    </span>
                                  )}
                                </div>
                              )}
                              <p data-testid={`cart-item-price-${it.cart_item_id}`} className="text-base font-bold mt-1.5">
                                {Number(it.price_total || 0).toFixed(2)} EUR
                              </p>
                            </div>
                            <button
                              data-testid={`cart-item-remove-${it.cart_item_id}`}
                              onClick={() => handleRemove(it.cart_item_id)}
                              disabled={removing[it.cart_item_id]}
                              className="p-2 rounded-full text-[#999] hover:text-red-600 hover:bg-[#FAFAFA] transition-colors disabled:opacity-50"
                              aria-label={t("cart.remove_aria")}>
                              {removing[it.cart_item_id] ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                            </button>
                          </div>
                          {it._unavailable && (
                            <div className="mt-2 flex items-center gap-1.5 text-[11px] text-red-600">
                              <AlertCircle size={11} />
                              {t("cart.unavailable")}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Bereichs-Footer: Zwischensumme + Bereichs-Checkout-Button */}
                <footer className="px-4 py-3 bg-[#FAFAFA] border-t border-[#E5E5E5] flex flex-col gap-3">
                  {/* iter238 · Purchase-Consent für SOCIAL (Variante B §6) */}
                  {area === "SOCIAL" && (
                    <PurchaseConsentCheckbox
                      checked={socialConsent}
                      onChange={setSocialConsent}
                      testId="cart-social-consent"
                    />
                  )}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex flex-col">
                      <span className="text-[10px] uppercase tracking-wider text-[#999]">Zwischensumme</span>
                      <span data-testid={`cart-section-total-${area}`} className="text-lg font-bold">{sectionTotal.toFixed(2)} EUR</span>
                    </div>
                    <button
                      data-testid={`cart-section-checkout-${area}`}
                      onClick={() => checkoutArea(area)}
                      disabled={hasUnavailable || hasLockedByOther || checkoutBusy === area || (area === "SOCIAL" && !socialConsent)}
                      className="h-10 px-5 rounded-full bg-[#0A0A0A] text-white text-sm font-medium disabled:opacity-40 hover:bg-[#222] flex items-center gap-2">
                      {checkoutBusy === area ? <Loader2 size={14} className="animate-spin" /> : null}
                      {hasLockedByOther
                        ? t("cart.checkout_blocked_locked", { defaultValue: "Aktuell reserviert – bitte warten" })
                        : `Zur Kasse für ${meta.label} (${items.length})`}
                    </button>
                  </div>
                </footer>
              </section>
            );
          })}

          {/* Gesamt-Anzeige (Referenz, kein Kombi-Checkout) */}
          <div className="sticky bottom-0 border-t border-[#E5E5E5] bg-white px-4 py-4 -mx-4 lg:-mx-6">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-sm text-[#666]">{t("cart.total")}</span>
              <span data-testid="cart-total-amount" className="text-2xl font-bold">
                {Number(cart.total_amount || 0).toFixed(2)} EUR
              </span>
            </div>
            <p className="text-[10px] text-[#999]">
              {t("cart.per_area_checkout_hint", { defaultValue: "Jeder Bereich wird separat abgerechnet — kein Kombi-Checkout über Bereiche hinweg." })}
            </p>
          </div>
        </div>
      )}

      {/* iter260 · Zahlungsart-Auswahl-Dialog (Social/Digital) */}
      {methodModal.open && (
        <div data-testid="social-payment-modal" className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4" onClick={() => !methodModal.busy && setMethodModal({ open: false, methods: [], busy: false })}>
          <div className="w-full max-w-md bg-white rounded-2xl p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-1" data-testid="social-payment-modal-title">
              {t("cart.choose_payment", { defaultValue: "Zahlungsart wählen" })}
            </h3>
            <p className="text-sm text-[#666] mb-4">
              {t("cart.choose_payment_hint", { defaultValue: "Wie möchtest Du für Deine digitalen Inhalte bezahlen?" })}
            </p>
            <div className="space-y-2">
              {methodModal.methods.map((code) => (
                <button
                  key={code}
                  data-testid={`social-payment-option-${code}`}
                  disabled={methodModal.busy}
                  onClick={() => doSocialCheckout(code)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-[#E5E5E5] hover:border-[#0A0A0A] hover:bg-[#FAFAFA] transition-colors disabled:opacity-50 text-left"
                >
                  <span>
                    <span className="block text-sm font-medium">{(METHOD_LABELS[code] || {}).label || code}</span>
                    <span className="block text-xs text-[#999]">{(METHOD_LABELS[code] || {}).sub || ""}</span>
                  </span>
                  {methodModal.busy ? <Loader2 size={16} className="animate-spin text-[#999]" /> : <span className="text-[#999]">›</span>}
                </button>
              ))}
            </div>
            <button
              data-testid="social-payment-cancel"
              disabled={methodModal.busy}
              onClick={() => setMethodModal({ open: false, methods: [], busy: false })}
              className="mt-4 w-full text-sm text-[#666] hover:text-[#0A0A0A] disabled:opacity-50"
            >
              {t("common.cancel", { defaultValue: "Abbrechen" })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
